<template>
  <ds
    ds="button"
    :label="$t('component.ConnectMetamask.connect')"
    iconSize="1rem"
    isReverse="true"
    :version="version"
    icon="open-to-right"
    :isDisabled="isDisabled"
    :isLoading="isSigning"
    @click="handleConnect"
  />
</template>
<script>
import { LoggerService } from '@/services';

import { EthereumClient, w3mConnectors, w3mProvider } from '@web3modal/ethereum';
import { configureChains, createConfig, getAccount, getNetwork, signMessage, watchAccount, watchNetwork, switchNetwork, disconnect } from '@wagmi/core';
import { Web3Modal } from '@web3modal/html';
import { mainnet, polygon, polygonMumbai, goerli, avalanche, avalancheFuji, telos, telosTestnet } from '@wagmi/core/chains';
import { polygonAmoy, klaytn, klaytnBaobab } from '@/chains';
import { ethereumNetworkInfo } from '@tokenyico/design-system';
import { getAddress } from 'viem';

const logo = require(`@/assets/themes/${process.env.VUE_APP_THEME_NAME}/logo.png`);

const networkName = process.env.VUE_APP_ETHEREUM_NETWORK;
const projectId = process.env.VUE_APP_WALLETCONNECT_PROJECT_ID;
const chains = [mainnet, polygon, polygonMumbai, polygonAmoy, goerli, avalanche, avalancheFuji, klaytn, klaytnBaobab, telos, telosTestnet];
const networkId = ethereumNetworkInfo.get(networkName).chainId;
const defaultChain = chains.find((chain) => chain.id === networkId);

const { publicClient } = configureChains(chains, [w3mProvider({ projectId })]);
const wagmiConfig = createConfig({
  autoConnect: true,
  connectors: w3mConnectors({ projectId, chains }),
  publicClient
});
const ethereumClient = new EthereumClient(wagmiConfig, chains);
const web3Modal = new Web3Modal({
  projectId,
  defaultChain,
  explorerRecommendedWalletIds: [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
    '19177a98252e07ddfc9af2083ba8e07ef627cb6103467ffebb3f8f4205fd7927', // Ledger
    '225affb176778569276e484e1b92637ad061b01e13a048b35a9d280c3b58970f', // Safe / Gnosis Safe
    '5864e2ced7c293ed18ac35e0db085c09ed567d67346ccb6f58a0327a75137489' // Fireblocks
  ],
  explorerExcludedWalletIds: 'ALL',
  themeMode: 'light',
  themeVariables: {
    '--w3m-accent-color': 'var(--primary)',
    '--w3m-background-color': '#FFFFFF',
    '--w3m-logo-image-url': logo,
    '--w3m-z-index': '9999',
    '--w3m-background-border-radius': '4px',
    '--w3m-container-border-radius': '4px',
    '--w3m-wallet-icon-border-radius': '4px',
    '--w3m-wallet-icon-large-border-radius': '4px',
    '--w3m-input-border-radius': '4px',
    '--w3m-button-border-radius': '4px',
    '--w3m-secondary-button-border-radius': '4px',
    '--w3m-notification-border-radius': '4px',
    '--w3m-icon-button-border-radius': '4px',
    '--w3m-button-hover-highlight-border-radius': '4px'
  }
}, ethereumClient);
const { isConnected, address, connector } = getAccount(); // set as initial state

export default {
  name: 'ConnectMetamask',

  props: {
    network: { type: String, default: process.env.VUE_APP_ETHEREUM_NETWORK },
    isDisabled: { type: Boolean, default: false },
    version: { type: String, default: 'secondary' }
  },

  data: () => {
    return {
      web3Provider: null,
      currentNetwork: null,
      selectedAccount: null,
      isSigning: false,
      web3Modal,
      isConnected,
      address,
      connector,
      web3ModalOpenCount: 0,
      unwatchAccount: null,
      unwatchNetwork: null
    };
  },
  mounted () {
    this.unwatchAccount = watchAccount((account) => {
      if (account.address && account.isConnected) {
        this.address = account.address;
      }
    });

    // Subscribe to chainId change
    this.unwatchNetwork = watchNetwork((_network) => {
      const chainId = '' + _network?.chain.id; // convert to string
      console.log('chainChanged', { chainId });
      this.handleChainChanged(chainId);
    });
  },
  unmounted () {
    this.unwatchAccount();
    this.unwatchNetwork();
  },
  methods: {
    async handleConnect () {
      const { isConnected, connector } = getAccount();
      this.isConnected = isConnected;
      this.connector = connector;

      try {
        if (!this.isConnected) {
          console.log('Opening a dialog');
          await this.web3Modal.openModal();
          this.web3ModalOpenCount += 1;
          // We want to subscribe to web3Modal event just 'one time' to call callback function and sing the message. Otherwise multiple event subscriptions will cause multiple callbacks.
          const unsubscribe = this.web3Modal.subscribeEvents((event) => {
            if (event.name === 'ACCOUNT_CONNECTED') {
              this.handleConnect(); // to continue signing message process after connecting the wallet
              this.web3ModalOpenCount = 0; // reset counting
              unsubscribe(); // delete last subscription
            };
          });
          if (this.web3ModalOpenCount > 1) unsubscribe();
          return;
        }

        if (!this.connector) return;

        let account;
        // wait for currently connected address
        // In this step, we display a list of available MetaMask accounts for you to choose when connecting to the app.
        if (connector.name === 'MetaMask') {
          account = await this.requestPermission();
        }

        const { chain: currentChain } = getNetwork();
        if (currentChain && currentChain.id === defaultChain.id) {
          const { address } = getAccount(); // get wallet address from wallet connector
          this.address = account ?? address;
          const message = `Proof of ownership: I here by declare I am the owner of the wallet: ${this.address}.`;
          this.isSigning = true; // start loading spinner
          const walletSignature = await signMessage({ message });
          this.selectedAccount = this.address;
          this.$emit('onWalletSignature', walletSignature);
          this.$emit('accountSelected', this.selectedAccount);
          this.web3Provider = this.connector.name; // for now only metamask
        } else {
          LoggerService.error(
            this.$t('component.ConnectMetamask.requestNetwork', {
              network: this.network
            }));
          await switchNetwork({ chainId: defaultChain.id });
        }
      } catch (error) {
        console.log('Could not get a wallet connection', error);
        this.$emit('accountSelected', this.selectedAccount);

        if (error.code === 4001) {
          // userRejectedRequest (EIP-1193 )
          LoggerService.error(
            this.$t('component.ConnectMetamask.requestConnectRejected')
          );
          disconnect();
        } else if (error.code === -32002) {
          // Resource unavailable (EIP-1474)
          LoggerService.error(
            this.$t('component.ConnectMetamask.requestConnectPending')
          );
        } else {
          console.log('unhandled Metamask error:');
          console.log(error); // @todo AG: send error details to Sentry
          LoggerService.error(error);
        }
      } finally {
        this.isSigning = false; // stop loading spinner
      }
    },
    requestPermission () {
      return new Promise((resolve, reject) => {
        this.connector.options.getProvider().request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        })
          .then((permissions) => {
            const accountsPermission = permissions.find(
              (permission) => permission.parentCapability === 'eth_accounts'
            );
            if (accountsPermission) {
              const address = accountsPermission.caveats[0].value[0];
              if (typeof address === 'string') { // prevent argument type error
                const checksumAddress = getAddress(address);
                resolve(checksumAddress);
              } else {
                resolve(undefined);
              }
            }
          })
          .catch((error) => {
            if (error.code === 4001) {
            // EIP-1193 userRejectedRequest error
              console.log('Permissions needed to continue.');
            } else {
              console.error(error);
            }
            disconnect();
            reject(error);
          });
      });
    },

    async  handleChainChanged (chainId) {
      switch (chainId) {
        case '1':
        case '0x1':
          this.currentNetwork = 'mainnet';
          break;

        case '3':
        case '0x3':
          this.currentNetwork = 'ropsten';
          break;

        case '42':
        case '0x2a':
          this.currentNetwork = 'kovan';
          break;

        case '137':
        case '0x89':
          this.currentNetwork = 'matic';
          break;

        case '80001':
        case '0x13881':
          this.currentNetwork = 'mumbai';
          break;

        case '80002':
        case '0x13882':
          this.currentNetwork = 'amoy';
          break;

        case '8217':
        case '0x2019':
          this.currentNetwork = 'klaytn';
          break;

        case '1001':
        case '0x3e9':
          this.currentNetwork = 'baobab';
          break;

        case '40':
        case '0x28':
          this.currentNetwork = 'telos';
          break;

        case '41':
        case '0x29':
          this.currentNetwork = 'telosTestnet';
          break;

        default:
      }
      console.log({ currentNetwork: this.currentNetwork });
      if (this.currentNetwork !== this.network) {
        LoggerService.error(
          this.$t('component.ConnectMetamask.requestNetwork', {
            network: this.network
          })
        );
        await switchNetwork({ chainId: defaultChain.id });
        return false;
      }
      return true;
    }
  }
};
</script>
